import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { enqueueSnackbar } from 'notistack';
import { useDispatch } from 'react-redux';
import { createExperience, updateExperience } from '../../actions/experienceAuth';

const CATEGORIES = [
  'Adventure',
  'Dining & Culinary',
  'Wellness & Spa',
  'Travel & Getaways',
  'Culture & Heritage',
  'Workshops & Learning',
  'Water Sports',
  'Outdoor & Nature',
  'Luxury Experiences',
  'Nightlife & Events',
];

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    bgcolor: '#fff',
    '& fieldset': { borderColor: alpha('#11409B', 0.2) },
    '&:hover fieldset': { borderColor: '#11409B' },
  },
};

const ExperienceFormModal = ({
  open,
  handleClose,
  experience = null,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const isEdit = Boolean(experience && (experience.id || experience._id));

  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('Adventure');
  const [location, setLocation] = useState('');
  const [city, setCity] = useState('');
  const [duration, setDuration] = useState('');
  const [pricing, setPricing] = useState('');
  const [discountPrice, setDiscountPrice] = useState('');
  const [slots, setSlots] = useState('');
  const [overview, setOverview] = useState('');
  const [itinerary, setItinerary] = useState('');
  const [guidelines, setGuidelines] = useState('');
  const [active, setActive] = useState(true);

  // Image tab: 0 for file upload, 1 for URL
  const [imageTab, setImageTab] = useState(0);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [imageUrl, setImageUrl] = useState('');

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (experience && open) {
      setTitle(experience.title || experience.name || '');
      setCategory(experience.category || 'Adventure');
      setLocation(experience.location || experience.venue || '');
      setCity(experience.city || '');
      setDuration(experience.duration || '');
      setPricing(experience.pricing ?? experience.price ?? experience.amount ?? '');
      setDiscountPrice(experience.discountPrice ?? experience.offerPrice ?? '');
      setSlots(experience.slots ?? experience.capacity ?? experience.totalSlots ?? '');
      setOverview(experience.overview || experience.description || '');
      setItinerary(experience.itinerary || '');
      setGuidelines(experience.guidelines || experience.terms || '');
      setActive(experience.active ?? experience.isActive ?? true);

      const existingImage = experience.imageUrl || experience.image || experience.banner || '';
      if (existingImage) {
        setImageUrl(existingImage);
        setImagePreview(existingImage);
        setImageTab(1);
      } else {
        setImageUrl('');
        setImagePreview('');
        setImageTab(0);
      }
      setImageFile(null);
    } else if (open) {
      // Reset form
      setTitle('');
      setCategory('Adventure');
      setLocation('');
      setCity('');
      setDuration('');
      setPricing('');
      setDiscountPrice('');
      setSlots('');
      setOverview('');
      setItinerary('');
      setGuidelines('');
      setActive(true);
      setImageTab(0);
      setImageFile(null);
      setImagePreview('');
      setImageUrl('');
    }
  }, [experience, open]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        enqueueSnackbar('Please select a valid image file', { variant: 'warning' });
        return;
      }
      setImageFile(file);
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview('');
    setImageUrl('');
  };

  const handleSubmit = (e) => {
    e?.preventDefault();

    if (!title.trim()) {
      enqueueSnackbar('Experience title is required', { variant: 'error' });
      return;
    }
    if (!category) {
      enqueueSnackbar('Category is required', { variant: 'error' });
      return;
    }
    if (!location.trim() && !city.trim()) {
      enqueueSnackbar('Location / City is required', { variant: 'error' });
      return;
    }
    if (!pricing || Number(pricing) <= 0) {
      enqueueSnackbar('Valid pricing amount is required', { variant: 'error' });
      return;
    }
    if (!slots || Number(slots) <= 0) {
      enqueueSnackbar('Valid slots / capacity is required', { variant: 'error' });
      return;
    }

    setLoading(true);

    const hasFile = imageTab === 0 && imageFile;

    if (hasFile) {
      const formData = new FormData();
      formData.append('title', title.trim());
      formData.append('category', category);
      formData.append('location', location.trim());
      formData.append('city', city.trim());
      formData.append('duration', duration.trim());
      formData.append('pricing', Number(pricing));
      if (discountPrice) formData.append('discountPrice', Number(discountPrice));
      formData.append('slots', Number(slots));
      formData.append('overview', overview.trim());
      formData.append('itinerary', itinerary.trim());
      formData.append('guidelines', guidelines.trim());
      formData.append('active', String(active));
      formData.append('file', imageFile);

      if (isEdit) {
        const id = experience.id || experience._id;
        dispatch(
          updateExperience(
            id,
            formData,
            true,
            () => {
              setLoading(false);
              enqueueSnackbar('Experience updated successfully', { variant: 'success' });
              handleClose();
              if (onSuccess) onSuccess();
            },
            (err) => {
              setLoading(false);
              const msg = err?.response?.data?.message || err?.message || 'Failed to update experience';
              enqueueSnackbar(msg, { variant: 'error' });
            }
          )
        );
      } else {
        dispatch(
          createExperience(
            formData,
            true,
            () => {
              setLoading(false);
              enqueueSnackbar('Experience created successfully', { variant: 'success' });
              handleClose();
              if (onSuccess) onSuccess();
            },
            (err) => {
              setLoading(false);
              const msg = err?.response?.data?.message || err?.message || 'Failed to create experience';
              enqueueSnackbar(msg, { variant: 'error' });
            }
          )
        );
      }
    } else {
      // JSON Payload
      const jsonPayload = {
        title: title.trim(),
        category,
        location: location.trim(),
        city: city.trim(),
        duration: duration.trim(),
        pricing: Number(pricing),
        ...(discountPrice ? { discountPrice: Number(discountPrice) } : {}),
        slots: Number(slots),
        overview: overview.trim(),
        itinerary: itinerary.trim(),
        guidelines: guidelines.trim(),
        active,
        ...(imageUrl.trim() ? { imageUrl: imageUrl.trim() } : {}),
      };

      if (isEdit) {
        const id = experience.id || experience._id;
        dispatch(
          updateExperience(
            id,
            jsonPayload,
            false,
            () => {
              setLoading(false);
              enqueueSnackbar('Experience updated successfully', { variant: 'success' });
              handleClose();
              if (onSuccess) onSuccess();
            },
            (err) => {
              setLoading(false);
              const msg = err?.response?.data?.message || err?.message || 'Failed to update experience';
              enqueueSnackbar(msg, { variant: 'error' });
            }
          )
        );
      } else {
        dispatch(
          createExperience(
            jsonPayload,
            false,
            () => {
              setLoading(false);
              enqueueSnackbar('Experience created successfully', { variant: 'success' });
              handleClose();
              if (onSuccess) onSuccess();
            },
            (err) => {
              setLoading(false);
              const msg = err?.response?.data?.message || err?.message || 'Failed to create experience';
              enqueueSnackbar(msg, { variant: 'error' });
            }
          )
        );
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '24px',
          boxShadow: '0 24px 60px rgba(15, 23, 42, 0.2)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 3,
          background: 'linear-gradient(135deg, #0c3483 0%, #11409B 50%, #1e40af 100%)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography variant="caption" sx={{ letterSpacing: '0.08em', opacity: 0.85, fontWeight: 700 }}>
            {isEdit ? 'EDIT EXPERIENCE' : 'ADD NEW EXPERIENCE'}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em', mt: 0.3 }}>
            {isEdit ? 'Update Experience Details' : 'Create Curated Experience'}
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          disabled={loading}
          sx={{
            color: '#fff',
            bgcolor: alpha('#fff', 0.12),
            '&:hover': { bgcolor: alpha('#fff', 0.24) },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3.5, bgcolor: '#f8fafc' }}>
        <Grid container spacing={2.5}>
          {/* Title */}
          <Grid item xs={12} sm={8}>
            <TextField
              label="Experience Title *"
              placeholder="e.g. Scuba Diving in Andaman"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              sx={fieldSx}
              disabled={loading}
            />
          </Grid>

          {/* Category */}
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth sx={fieldSx}>
              <InputLabel>Category *</InputLabel>
              <Select
                value={category}
                label="Category *"
                onChange={(e) => setCategory(e.target.value)}
                disabled={loading}
              >
                {CATEGORIES.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Location & City */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Location / Venue *"
              placeholder="e.g. Elephant Beach, Jetty #3"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              fullWidth
              sx={fieldSx}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="City / Destination *"
              placeholder="e.g. Havelock Island, Goa, Manali"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              fullWidth
              sx={fieldSx}
              disabled={loading}
            />
          </Grid>

          {/* Duration, Pricing, Discount, Slots */}
          <Grid item xs={12} sm={3}>
            <TextField
              label="Duration"
              placeholder="e.g. 3 Hours, 2 Days"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              fullWidth
              sx={fieldSx}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              label="Base Price (₹) *"
              type="number"
              placeholder="e.g. 2999"
              value={pricing}
              onChange={(e) => setPricing(e.target.value)}
              fullWidth
              sx={fieldSx}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              label="Discount Price (₹)"
              type="number"
              placeholder="e.g. 2499"
              value={discountPrice}
              onChange={(e) => setDiscountPrice(e.target.value)}
              fullWidth
              sx={fieldSx}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              label="Slots / Capacity *"
              type="number"
              placeholder="e.g. 20"
              value={slots}
              onChange={(e) => setSlots(e.target.value)}
              fullWidth
              sx={fieldSx}
              disabled={loading}
            />
          </Grid>

          {/* Overview */}
          <Grid item xs={12}>
            <TextField
              label="Overview & Description"
              placeholder="Describe this curated experience, highlights, what makes it special..."
              value={overview}
              onChange={(e) => setOverview(e.target.value)}
              fullWidth
              multiline
              rows={3}
              sx={fieldSx}
              disabled={loading}
            />
          </Grid>

          {/* Itinerary */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Itinerary / Schedule"
              placeholder="Schedule of events (e.g. 10:00 AM Welcome drink, 11:00 AM Dive session, 01:00 PM Lunch)"
              value={itinerary}
              onChange={(e) => setItinerary(e.target.value)}
              fullWidth
              multiline
              rows={4}
              sx={fieldSx}
              disabled={loading}
            />
          </Grid>

          {/* Guidelines */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Guidelines & Requirements"
              placeholder="Safety instructions, age restrictions, items to bring, cancellation policy..."
              value={guidelines}
              onChange={(e) => setGuidelines(e.target.value)}
              fullWidth
              multiline
              rows={4}
              sx={fieldSx}
              disabled={loading}
            />
          </Grid>

          {/* Image Upload Section */}
          <Grid item xs={12}>
            <Box
              sx={{
                p: 2.5,
                bgcolor: '#fff',
                borderRadius: '16px',
                border: '1px solid',
                borderColor: alpha('#11409B', 0.12),
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 1 }}>
                Experience Image / Banner
              </Typography>

              <Tabs
                value={imageTab}
                onChange={(_, v) => setImageTab(v)}
                sx={{ mb: 2, minHeight: 36, '& .MuiTab-root': { py: 0.5, minHeight: 36, textTransform: 'none' } }}
              >
                <Tab icon={<CloudUploadOutlinedIcon fontSize="small" />} iconPosition="start" label="Upload File" />
                <Tab icon={<LinkOutlinedIcon fontSize="small" />} iconPosition="start" label="Image URL" />
              </Tabs>

              {imageTab === 0 ? (
                <Box>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUploadOutlinedIcon />}
                    disabled={loading}
                    sx={{
                      borderRadius: '12px',
                      textTransform: 'none',
                      fontWeight: 600,
                      borderColor: alpha('#11409B', 0.3),
                      color: '#11409B',
                    }}
                  >
                    Select Image File
                    <input type="file" accept="image/*" hidden onChange={handleFileChange} />
                  </Button>
                  {imageFile && (
                    <Typography variant="caption" sx={{ ml: 2, color: '#64748b' }}>
                      {imageFile.name} ({(imageFile.size / 1024).toFixed(1)} KB)
                    </Typography>
                  )}
                </Box>
              ) : (
                <TextField
                  label="Direct Image URL"
                  placeholder="https://images.unsplash.com/..."
                  value={imageUrl}
                  onChange={(e) => {
                    setImageUrl(e.target.value);
                    setImagePreview(e.target.value);
                  }}
                  fullWidth
                  sx={fieldSx}
                  disabled={loading}
                />
              )}

              {/* Preview */}
              {imagePreview && (
                <Box sx={{ mt: 2, position: 'relative', display: 'inline-block' }}>
                  <Box
                    component="img"
                    src={imagePreview}
                    alt="Preview"
                    sx={{
                      width: '100%',
                      maxHeight: 180,
                      objectFit: 'cover',
                      borderRadius: '12px',
                      border: '1px solid rgba(0,0,0,0.1)',
                    }}
                    onError={() => {
                      enqueueSnackbar('Unable to load preview image URL', { variant: 'warning' });
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={handleRemoveImage}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      bgcolor: 'rgba(0,0,0,0.6)',
                      color: '#fff',
                      '&:hover': { bgcolor: 'rgba(220,38,38,0.85)' },
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
          </Grid>

          {/* Active Status Switch */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                  color="primary"
                  disabled={loading}
                />
              }
              label={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Active Status
                  </Typography>
                  <Chip
                    label={active ? 'Active' : 'Inactive'}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      bgcolor: active ? alpha('#16a34a', 0.12) : alpha('#dc2626', 0.12),
                      color: active ? '#16a34a' : '#dc2626',
                    }}
                  />
                </Stack>
              }
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, bgcolor: '#fff', borderTop: '1px solid rgba(0,0,0,0.06)' }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 600,
            color: '#64748b',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 700,
            px: 3.5,
            background: 'linear-gradient(135deg, #0c3483 0%, #11409B 100%)',
            boxShadow: '0 4px 16px rgba(17, 64, 155, 0.25)',
          }}
        >
          {loading ? (
            <CircularProgress size={22} sx={{ color: '#fff' }} />
          ) : isEdit ? (
            'Update Experience'
          ) : (
            'Create Experience'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExperienceFormModal;
